import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { notifyUpdate } from '../../lib/api';
import { CITIES, IMG } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';

const empty = {
  title: '', typeId: '', categoryId: '', city: '', shortDesc: '', description: '',
  image: IMG.lake1, gallery: [], lat: 44.15, lng: 17.65, address: '', phone: '', email: '', website: '', bookingUrl: '', featured: false, status: 'active'
};

export default function AdminPonuda() {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [o, t, c] = await Promise.all([api.get('/offers/all'), api.get('/types'), api.get('/categories')]);
        setOffers(o.data);
        setTypes(t.data);
        setCategories(c.data);
      } catch (err) {
        console.error('Greška pri učitavanju:', err);
      }
    };
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const visible = user?.role === 'sluzbenik' ? offers.filter((o) => o.city === user.city) : offers;
  const cats = form.typeId ? categories.filter((c) => c.typeId === form.typeId) : categories;

  const openNew = () => {
    setForm({ ...empty, city: user?.role === 'sluzbenik' ? user.city : '' });
    setEditId(null); setOpen(true);
  };
  const openEdit = (o) => { setForm({ ...empty, ...o }); setEditId(o.id); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.typeId || !form.categoryId || !form.city || !form.shortDesc) {
      toast.error('Popunite naziv, tip, kategoriju, grad i kratki opis.');
      return;
    }
    const payload = {
      ...form,
      lat: parseFloat(form.lat), lng: parseFloat(form.lng),
      gallery: form.gallery && form.gallery.length ? form.gallery : [form.image],
      description: form.description || form.shortDesc
    };
    try {
      if (editId) {
        await api.put(`/offers/${editId}`, payload);
        toast.success('Turistička ponuda je ažurirana.');
      } else {
        await api.post('/offers', payload);
        toast.success('Turistička ponuda je dodana.');
      }
      notifyUpdate();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/offers/${id}`);
      toast.success('Stavka je obrisana iz ponude.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri brisanju.');
    }
  };

  return (
    <div data-testid="admin-ponuda">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1a3d]">Turistička ponuda</h1>
          <p className="text-sm text-slate-500">
            {user?.role === 'sluzbenik' ? `Upravljanje ponudom za: ${user.city}` : 'Upravljanje kompletnom turističkom ponudom kantona.'}
          </p>
        </div>
        <Button onClick={openNew} className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200" data-testid="add-offer-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova stavka
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#f8f9fd]">
              <TableHead className="text-[#001752] font-semibold">Naziv</TableHead>
              <TableHead className="text-[#001752] font-semibold hidden md:table-cell">Kategorija</TableHead>
              <TableHead className="text-[#001752] font-semibold w-40">Grad/Općina</TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((o) => {
              const cat = categories.find((c) => c.id === o.categoryId);
              return (
                <TableRow key={o.id} data-testid={`offer-row-${o.id}`}>
                  <TableCell className="font-medium text-slate-700">
                    <div className="flex items-center gap-2">
                      <img src={o.image} alt="" className="h-9 w-12 rounded object-cover hidden sm:block" />
                      <span className="truncate max-w-[220px]">{o.title}</span>
                      {o.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 hidden md:table-cell">{cat?.name || '-'}</TableCell>
                  <TableCell className="text-slate-500">{o.city}</TableCell>
                  <TableCell>
                    <div className="flex gap-1.5 justify-end">
                      <Button size="icon" variant="outline" onClick={() => openEdit(o)} data-testid={`edit-offer-${o.id}`}
                        className="h-8 w-8 border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => remove(o.id)} data-testid={`delete-offer-${o.id}`}
                        className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="offer-dialog-form">
          <DialogHeader>
            <DialogTitle className="text-[#0b1a3d]">{editId ? 'Uredi stavku ponude' : 'Nova stavka ponude'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Naziv *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-form-title" className="border-slate-200" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">Tip turizma *</Label>
                <Select value={form.typeId} onValueChange={(v) => setForm({ ...form, typeId: v, categoryId: '' })}>
                  <SelectTrigger className="border-slate-200" data-testid="offer-form-type"><SelectValue placeholder="Odaberite" /></SelectTrigger>
                  <SelectContent>{types.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Kategorija *</Label>
                <Select value={form.categoryId} onValueChange={(v) => setForm({ ...form, categoryId: v })}>
                  <SelectTrigger className="border-slate-200" data-testid="offer-form-category"><SelectValue placeholder="Odaberite" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Grad/Općina *</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })} disabled={user?.role === 'sluzbenik'}>
                  <SelectTrigger className="border-slate-200" data-testid="offer-form-city"><SelectValue placeholder="Odaberite" /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Kratki opis *</Label>
              <Textarea rows={2} value={form.shortDesc} onChange={(e) => setForm({ ...form, shortDesc: e.target.value })} data-testid="offer-form-shortdesc" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Detaljan opis</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-slate-200" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">URL slike</Label>
                <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">GPS širina (lat)</Label>
                <Input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="offer-form-lat" className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">GPS dužina (lng)</Label>
                <Input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="offer-form-lng" className="border-slate-200" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">Adresa</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border-slate-200" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">E-mail</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Link za rezervaciju (booking)</Label>
                <Input value={form.bookingUrl} onChange={(e) => setForm({ ...form, bookingUrl: e.target.value })} className="border-slate-200" placeholder="https://..." />
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Checkbox id="featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} />
              <Label htmlFor="featured" className="text-sm text-slate-600 cursor-pointer">Istakni na naslovnoj stranici</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={save} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="offer-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
